// Records the voice of the user for the CandyVoice voice translation: they read
// aloud 25 sentences of their language in a single recording (about 3
// minutes), and the translation of what they say in a meeting is then read with
// their own voice. Opened from the user menu, see Header. Same steps as the
// demo application of CandyVoice. See src/lib/candyvoice.ts
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { atom, useAtom } from "jotai";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons/faMicrophone";
import { faStop } from "@fortawesome/free-solid-svg-icons/faStop";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons/faCircleQuestion";
import { LanguageSelect, toSupportedLanguage } from "../Room/LanguageSelect";
import {
  deleteVoice,
  enrollmentLanguages,
  EnrollError,
  enrollVoice,
  fetchCorpus,
  fetchVoice,
  useCandyVoiceLanguages,
  Voice,
} from "../../lib/candyvoice";
import { colors } from "../../lib/constants";
import type { Options, RecordRTCPromisesHandler } from "recordrtc";

const { purple, greyBlue, deepBlue } = colors;

export const voiceEnrollmentModalOpenAtom = atom(false);

// CandyVoice takes a recording of 30 seconds to 10 minutes
const MIN_SECONDS = 30;
const MAX_SECONDS = 10 * 60;

type Step =
  | "loading"
  | "howItWorks"
  | "intro"
  | "read"
  | "uploading"
  | "done"
  | "manage";

const HOW_IT_WORKS_SEEN_KEY = "voiceEnrollmentHowItWorksSeen";

// The same recorder as the deepfake detection (VoiceCheck.tsx): a mono wav at
// the native sample rate. No desiredSampRate, RecordRTC downsamples without an
// anti-aliasing filter and the aliasing could make the voice look synthetic to
// the deepfake check of CandyVoice. About 17 MB for 3 minutes at 48 kHz.
const recorderConfig: Options = {
  mimeType: "audio/wav",
  numberOfAudioChannels: 1, // mono
  type: "audio",
};

// The connection was cut before the server answered, but CandyVoice may
// still be recording the voice (it takes up to 3 minutes): ask a few times
// whether a new voice is there, for a minute and a half
const waitForNewVoice = async (previous: Voice | null) => {
  for (let attempt = 0; attempt < 6; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 15000));
    try {
      const voice = await fetchVoice();
      const isNew =
        voice.complete &&
        !(previous?.complete && previous.seconds === voice.seconds);
      if (isNew) return voice;
    } catch (error) {
      console.error("voice enrollment: failed to fetch the voice", error);
    }
  }
  return null;
};

const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      color: purple,
      fontSize: "12px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const Title = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="h2"
    sx={{ color: deepBlue, fontSize: "24px", fontWeight: 700, mb: 1.5 }}
  >
    {children}
  </Typography>
);

const Notice = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      borderLeft: `3px solid ${purple}`,
      backgroundColor: "#f3ecfd",
      borderRadius: "6px",
      padding: "10px 14px",
      fontSize: "14px",
      color: greyBlue,
      my: 2,
    }}
  >
    {children}
  </Box>
);

type ModalError = EnrollError | "mic" | "corpus" | "too_short";

const ErrorMessage = ({ error }: { error: ModalError }) => {
  let message;
  switch (error) {
    case "synthetic_voice":
      message = (
        <FormattedMessage
          id="voice-enrollment.error-synthetic"
          defaultMessage="This recording sounds like a synthetic voice, it was not kept. Please read the sentences aloud yourself, then record again."
        />
      );
      break;
    case "unavailable":
      message = (
        <FormattedMessage
          id="voice-enrollment.error-unavailable"
          defaultMessage="Your voice can't be checked right now. Please try again in a few minutes."
        />
      );
      break;
    case "too_many_speakers":
      message = (
        <FormattedMessage
          id="voice-enrollment.error-too-many-speakers"
          defaultMessage="The maximum number of recorded voices is reached. Please contact us."
        />
      );
      break;
    case "too_short":
      message = (
        <FormattedMessage
          id="voice-enrollment.error-invalid"
          defaultMessage="The recording must last between 30 seconds and 10 minutes."
        />
      );
      break;
    case "mic":
      message = (
        <FormattedMessage
          id="room.please-allow-mic-permission"
          defaultMessage="Please allow permission to use the mic"
        />
      );
      break;
    default:
      message = (
        <FormattedMessage
          id="voice-enrollment.error-failed"
          defaultMessage="Your voice could not be recorded. Please try again."
        />
      );
  }
  return (
    <Typography sx={{ color: colors.red, fontSize: "14px", mt: 1 }}>
      {message}
    </Typography>
  );
};

const PrimaryButton = (props: React.ComponentProps<typeof Button>) => (
  <Button
    variant="contained"
    {...props}
    sx={{ textTransform: "none", fontWeight: 600, ...props.sx }}
  />
);

const SecondaryButton = (props: React.ComponentProps<typeof Button>) => (
  <Button
    variant="outlined"
    {...props}
    sx={{ textTransform: "none", fontWeight: 600, ...props.sx }}
  />
);

const HowItWorks = ({ onDone }: { onDone: () => void }) => {
  const steps = [
    {
      title: (
        <FormattedMessage
          id="voice-enrollment.how-step1-title"
          defaultMessage="Create your voice model"
        />
      ),
      text: (
        <FormattedMessage
          id="voice-enrollment.how-step1-text"
          defaultMessage="Before your first translation, choose your language and read aloud the sentences shown, in a single recording (about 3 minutes, somewhere quiet). Your voice model is created from this recording. It is kept until you delete it, with the “Delete my voice” button."
        />
      ),
    },
    {
      title: (
        <FormattedMessage
          id="voice-enrollment.how-step2-title"
          defaultMessage="Choose your languages"
        />
      ),
      text: (
        <FormattedMessage
          id="voice-enrollment.how-step2-text"
          defaultMessage="In the meeting, choose the language you speak and the language you want to hear the others in."
        />
      ),
    },
    {
      title: (
        <FormattedMessage
          id="voice-enrollment.how-step3-title"
          defaultMessage="Speak"
        />
      ),
      text: (
        <FormattedMessage
          id="voice-enrollment.how-step3-text"
          defaultMessage="Once the moderator enabled the translation, what you say is translated sentence by sentence (use a headset)."
        />
      ),
    },
    {
      title: (
        <FormattedMessage
          id="voice-enrollment.how-step4-title"
          defaultMessage="The others hear you in their language"
        />
      ),
      text: (
        <FormattedMessage
          id="voice-enrollment.how-step4-text"
          defaultMessage="They read the transcription and its translation, and hear the translation read with your voice."
        />
      ),
    },
  ];
  return (
    <>
      <Eyebrow>
        <FormattedMessage
          id="voice-enrollment.getting-started"
          defaultMessage="Getting started"
        />
      </Eyebrow>
      <Title>
        <FormattedMessage
          id="voice-enrollment.how-title"
          defaultMessage="How voice translation works"
        />
      </Title>
      <Typography sx={{ mb: 2 }}>
        <FormattedMessage
          id="voice-enrollment.how-intro"
          defaultMessage="Your translations are read with your own voice. Here is how."
        />
      </Typography>
      <Stack spacing={1.5}>
        {steps.map((step, index) => (
          <Stack direction="row" spacing={1.5} key={index}>
            <Box
              sx={{
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#f3ecfd",
                color: purple,
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {index + 1}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, color: deepBlue }}>
                {step.title}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: greyBlue }}>
                {step.text}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
      <Box component="ul" sx={{ fontSize: "14px", pl: 2.5, mt: 2 }}>
        <li>
          <FormattedMessage
            id="voice-enrollment.how-note-model"
            defaultMessage="Your voice model only comes from the sentences you read: what you say in the meetings is not kept."
          />
        </li>
        <li>
          <FormattedMessage
            id="voice-enrollment.how-note-rerecord"
            defaultMessage="You can record your voice again at any time with “Record my voice again”, in the user menu."
          />
        </li>
      </Box>
      <PrimaryButton onClick={onDone} sx={{ mt: 2 }}>
        <FormattedMessage id="manage-avatars.got-it" defaultMessage="Got it" />
      </PrimaryButton>
    </>
  );
};

// The microphones, like MicSelect in the meetings, whose last choice it shares
// (lastUsedMicDeviceId). Their names are only known with the permission to use
// the microphone, asked for when it is not given yet.
const useMicDevices = () => {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    let cancelled = false;
    const update = async () => {
      let inputs = (await navigator.mediaDevices.enumerateDevices()).filter(
        (device) => device.kind === "audioinput"
      );
      if (inputs.some((device) => !device.label)) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          stream.getTracks().forEach((track) => track.stop());
          inputs = (await navigator.mediaDevices.enumerateDevices()).filter(
            (device) => device.kind === "audioinput"
          );
        } catch (error) {
          // refused: the recording tells it
        }
      }
      if (!cancelled) setDevices(inputs.filter((device) => device.label));
    };
    update();
    navigator.mediaDevices.addEventListener("devicechange", update);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener("devicechange", update);
    };
  }, []);
  return devices;
};

const MicSelect = ({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (deviceId: string) => void;
  disabled: boolean;
}) => {
  const devices = useMicDevices();
  // the last one used, or the one of the system
  const selected = devices.some((device) => device.deviceId === value)
    ? value
    : devices[0]?.deviceId ?? "";
  useEffect(() => {
    if (selected !== value) onChange(selected);
  }, [selected, value, onChange]);
  if (devices.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
      <Typography
        component="label"
        htmlFor="voiceEnrollmentMic"
        sx={{ fontWeight: 600, fontSize: "14px" }}
      >
        <FormattedMessage id="room.microphone" defaultMessage="Microphone" />
      </Typography>
      <Select
        id="voiceEnrollmentMic"
        size="small"
        value={selected}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        sx={{ flex: 1, minWidth: 0, fontSize: "14px" }}
      >
        {devices.map((device) => (
          <MenuItem key={device.deviceId} value={device.deviceId}>
            {device.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};

export const VoiceEnrollmentModal = () => {
  const intl = useIntl();
  const [open, setOpen] = useAtom(voiceEnrollmentModalOpenAtom);
  const [step, setStep] = useState<Step>("loading");
  const [voice, setVoice] = useState<Voice | null>(null);
  const candyVoiceLanguages = useCandyVoiceLanguages(open);
  const languages = useMemo(
    () => enrollmentLanguages(candyVoiceLanguages),
    [candyVoiceLanguages]
  );
  const [language, setLanguage] = useState(intl.locale);
  // the UI language until the languages arrive
  useEffect(() => {
    setLanguage((language) => toSupportedLanguage(language, languages));
  }, [languages]);
  const [consent, setConsent] = useState(false);
  const [sentences, setSentences] = useState<string[]>([]);
  const [error, setError] = useState<ModalError | null>(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const recorderRef = useRef<RecordRTCPromisesHandler | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [micDeviceId, setMicDeviceId] = useState(
    () => localStorage.getItem("lastUsedMicDeviceId") ?? ""
  );
  const changeMic = useCallback((deviceId: string) => {
    setMicDeviceId(deviceId);
    if (deviceId) localStorage.setItem("lastUsedMicDeviceId", deviceId);
  }, []);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // the length of the recording, read when it stops
  const secondsRef = useRef(0);

  // Stops the recording, and returns it unless discard (the modal is closed
  // while recording)
  const finishRecording = useCallback(async (discard: boolean) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    const recorder = recorderRef.current;
    recorderRef.current = null;
    let blob: Blob | null = null;
    if (recorder) {
      try {
        await recorder.stopRecording();
        if (!discard) blob = await recorder.getBlob();
      } catch (error) {
        console.error("voice enrollment: failed to stop the recorder", error);
      }
      recorder.destroy().catch(() => {});
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setRecording(false);
    return blob;
  }, []);

  // what to show when opened: the voice already recorded, or how it works the
  // first time
  useEffect(() => {
    if (!open) return;
    setStep("loading");
    setError(null);
    setConfirmDelete(false);
    fetchVoice()
      .then((voice) => {
        setVoice(voice);
        if (voice.complete) {
          if (voice.language) setLanguage(voice.language);
          setStep("manage");
        } else if (localStorage.getItem(HOW_IT_WORKS_SEEN_KEY)) {
          setStep("intro");
        } else {
          setStep("howItWorks");
        }
      })
      .catch((error) => {
        console.error("voice enrollment: failed to fetch the voice", error);
        setVoice(null);
        setStep("intro");
      });
  }, [open]);

  const close = useCallback(() => {
    finishRecording(true);
    setOpen(false);
  }, [setOpen, finishRecording]);

  useEffect(() => close, [close]);

  const showSentences = async () => {
    setError(null);
    try {
      setSentences(await fetchCorpus(language));
      setStep("read");
    } catch (error) {
      console.error("voice enrollment: failed to fetch the corpus", error);
      setError("corpus");
    }
  };

  const upload = async (audio: Blob) => {
    setStep("uploading");
    const previous = voice;
    try {
      setVoice(await enrollVoice(language, audio));
      setStep("done");
    } catch (error) {
      console.error("voice enrollment: failed to record the voice", error);
      const recorded =
        error === "no_answer" ? await waitForNewVoice(previous) : null;
      if (recorded) {
        setVoice(recorded);
        setStep("done");
      } else {
        setError(error === "no_answer" ? "failed" : (error as EnrollError));
        setStep("read");
      }
    }
  };

  const startRecording = async () => {
    setError(null);
    let stream: MediaStream;
    try {
      // the voice as it is: CandyVoice filters the background noise itself
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: micDeviceId ? { exact: micDeviceId } : undefined,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
    } catch (error) {
      console.error("voice enrollment: no microphone", error);
      setError("mic");
      return;
    }
    streamRef.current = stream;
    try {
      const {
        default: { RecordRTCPromisesHandler, StereoAudioRecorder },
      } = await import("recordrtc");
      const recorder = new RecordRTCPromisesHandler(stream, {
        ...recorderConfig,
        recorderType: StereoAudioRecorder,
      });
      await recorder.startRecording();
      recorderRef.current = recorder;
    } catch (error) {
      console.error("voice enrollment: failed to start the recorder", error);
      await finishRecording(true);
      setError("failed");
      return;
    }
    setSeconds(0);
    secondsRef.current = 0;
    setRecording(true);
    timerRef.current = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
      if (secondsRef.current >= MAX_SECONDS) stopRecording();
    }, 1000);
  };

  const stopRecording = async () => {
    const elapsed = secondsRef.current;
    const blob = await finishRecording(false);
    if (!blob) return;
    if (elapsed < MIN_SECONDS) {
      setError("too_short");
      return;
    }
    upload(blob);
  };

  const removeVoice = async () => {
    try {
      await deleteVoice();
      setVoice({ complete: false, language: null });
      setConfirmDelete(false);
      setStep("intro");
    } catch (error) {
      console.error("voice enrollment: failed to delete the voice", error);
      setError("failed");
    }
  };

  const languageLabel = (code: string | null) => {
    if (!code) return "";
    try {
      return new Intl.DisplayNames([intl.locale], { type: "language" }).of(
        code
      );
    } catch (e) {
      return code;
    }
  };

  let content;
  switch (step) {
    case "loading":
      content = (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
      break;
    case "howItWorks":
      content = (
        <HowItWorks
          onDone={() => {
            localStorage.setItem(HOW_IT_WORKS_SEEN_KEY, "true");
            setStep(voice?.complete ? "manage" : "intro");
          }}
        />
      );
      break;
    case "intro":
      content = (
        <>
          <Eyebrow>
            <FormattedMessage
              id="voice-enrollment.your-voice"
              defaultMessage="Your voice"
            />
          </Eyebrow>
          <Title>
            <FormattedMessage
              id="voice-enrollment.title"
              defaultMessage="Record your voice"
            />
          </Title>
          <Typography sx={{ mb: 2 }}>
            <FormattedMessage
              id="voice-enrollment.intro"
              defaultMessage="So that your translations are read with your own voice, read aloud 25 short sentences in your language, in a single recording. It takes about 3 minutes."
            />
          </Typography>
          <Typography
            component="label"
            sx={{ fontWeight: 600, fontSize: "14px" }}
          >
            <FormattedMessage
              id="voice-enrollment.your-language"
              defaultMessage="Your language"
            />
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <LanguageSelect
              id="voiceEnrollmentLanguage"
              languages={languages}
              value={language}
              onChange={setLanguage}
              menuPlacement="bottom"
            />
          </Box>
          <Notice>
            <FormattedMessage
              id="voice-enrollment.kept-notice"
              defaultMessage="This recording is kept on the servers of CandyVoice until you delete it, with the “Delete my voice” button of this window. It is the only recording of your voice that is kept: what you say in the meetings is not kept."
            />
          </Notice>
          <Box component="ul" sx={{ fontSize: "14px", pl: 2.5, mt: 0 }}>
            <li>
              <FormattedMessage
                id="voice-enrollment.tip-quiet"
                defaultMessage="Settle somewhere quiet, and speak at your usual volume and pace."
              />
            </li>
            <li>
              <FormattedMessage
                id="voice-enrollment.tip-read-all"
                defaultMessage="Read all the sentences in order, as they are written, then stop the recording."
              />
            </li>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px" }}>
                <FormattedMessage
                  id="voice-enrollment.consent"
                  defaultMessage="I agree that my voice is kept to read the translation of what I say with my own voice."
                />
              </Typography>
            }
          />
          {error && (
            <ErrorMessage error={error === "corpus" ? "failed" : error} />
          )}
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <PrimaryButton disabled={!consent} onClick={showSentences}>
              <FormattedMessage
                id="voice-enrollment.start"
                defaultMessage="Start"
              />
            </PrimaryButton>
            <SecondaryButton onClick={close}>
              <FormattedMessage
                id="voice-enrollment.later"
                defaultMessage="Later"
              />
            </SecondaryButton>
          </Stack>
        </>
      );
      break;
    case "read":
      content = (
        <>
          <Eyebrow>
            <FormattedMessage
              id="voice-enrollment.your-voice"
              defaultMessage="Your voice"
            />
          </Eyebrow>
          <Title>
            <FormattedMessage
              id="voice-enrollment.title"
              defaultMessage="Record your voice"
            />
          </Title>
          <Typography sx={{ fontSize: "13px", color: greyBlue, mb: 1 }}>
            <FormattedMessage
              id="voice-enrollment.read-instructions"
              defaultMessage="Start the recording, then read aloud these {count} sentences, one after the other:"
              values={{ count: sentences.length }}
            />
          </Typography>
          <Box
            component="ol"
            sx={{
              borderLeft: `3px solid ${purple}`,
              backgroundColor: "#faf7ff",
              borderRadius: "6px",
              maxHeight: "40vh",
              overflowY: "auto",
              m: 0,
              py: 1,
              pl: 5,
              pr: 2,
              fontSize: "17px",
              color: deepBlue,
              "& li": { py: 0.5 },
              "& li::marker": { color: purple, fontSize: "13px" },
            }}
          >
            {sentences.map((sentence, index) => (
              <li key={index}>{sentence}</li>
            ))}
          </Box>
          <MicSelect
            value={micDeviceId}
            onChange={changeMic}
            disabled={recording}
          />
          {error && <ErrorMessage error={error} />}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mt: 2 }}
          >
            {recording ? (
              <PrimaryButton
                color="error"
                startIcon={<FontAwesomeIcon icon={faStop} />}
                onClick={stopRecording}
              >
                <FormattedMessage
                  id="voice-enrollment.stop-recording"
                  defaultMessage="Stop the recording"
                />
              </PrimaryButton>
            ) : (
              <PrimaryButton
                startIcon={<FontAwesomeIcon icon={faMicrophone} />}
                onClick={startRecording}
              >
                <FormattedMessage
                  id="voice-enrollment.record"
                  defaultMessage="Record the voice"
                />
              </PrimaryButton>
            )}
            {recording && (
              <Typography
                sx={{
                  color: seconds < MIN_SECONDS ? greyBlue : purple,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <Box component="span" sx={{ color: colors.red, mr: 0.5 }}>
                  ●
                </Box>
                {formatDuration(seconds)}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
            <SecondaryButton
              disabled={recording}
              onClick={() => setStep("intro")}
            >
              <FormattedMessage
                id="actions.change-language"
                defaultMessage="Change language"
              />
            </SecondaryButton>
            <SecondaryButton onClick={close}>
              <FormattedMessage
                id="voice-enrollment.later"
                defaultMessage="Later"
              />
            </SecondaryButton>
          </Stack>
        </>
      );
      break;
    case "uploading":
      content = (
        <>
          <Eyebrow>
            <FormattedMessage
              id="voice-enrollment.your-voice"
              defaultMessage="Your voice"
            />
          </Eyebrow>
          <Title>
            <FormattedMessage
              id="voice-enrollment.title"
              defaultMessage="Record your voice"
            />
          </Title>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={24} />
            <Typography>
              <FormattedMessage
                id="voice-enrollment.uploading"
                defaultMessage="Creating your voice model, this can take a minute…"
              />
            </Typography>
          </Stack>
        </>
      );
      break;
    case "done":
      content = (
        <>
          <Eyebrow>
            <FormattedMessage
              id="voice-enrollment.your-voice"
              defaultMessage="Your voice"
            />
          </Eyebrow>
          <Title>
            <FormattedMessage
              id="voice-enrollment.title"
              defaultMessage="Record your voice"
            />
          </Title>
          <Typography sx={{ mb: 2 }}>
            <FormattedMessage
              id="voice-enrollment.done"
              defaultMessage="Your voice is recorded. Your translations will now be read with your own voice."
            />
          </Typography>
          <PrimaryButton onClick={close}>
            <FormattedMessage
              id="voice-enrollment.start-translating"
              defaultMessage="Start translating"
            />
          </PrimaryButton>
        </>
      );
      break;
    case "manage":
      content = (
        <>
          <Eyebrow>
            <FormattedMessage
              id="voice-enrollment.your-voice"
              defaultMessage="Your voice"
            />
          </Eyebrow>
          <Title>
            <FormattedMessage
              id="voice-enrollment.manage-title"
              defaultMessage="Your voice is recorded"
            />
          </Title>
          <Typography sx={{ mb: 2 }}>
            <FormattedMessage
              id="voice-enrollment.manage-text"
              defaultMessage="The translation of what you say in the meetings is read with your own voice, recorded in {language}."
              values={{ language: languageLabel(voice?.language ?? language) }}
            />
          </Typography>
          {error && <ErrorMessage error={error} />}
          {confirmDelete ? (
            <Notice>
              <Typography sx={{ fontSize: "14px", mb: 1 }}>
                <FormattedMessage
                  id="voice-enrollment.confirm-delete"
                  defaultMessage="Delete your voice? The translation of what you say will be read with a standard synthetic voice."
                />
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <PrimaryButton color="error" onClick={removeVoice}>
                  <FormattedMessage
                    id="voice-enrollment.delete"
                    defaultMessage="Delete my voice"
                  />
                </PrimaryButton>
                <SecondaryButton onClick={() => setConfirmDelete(false)}>
                  <FormattedMessage
                    id="actions.cancel"
                    defaultMessage="Cancel"
                  />
                </SecondaryButton>
              </Stack>
            </Notice>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                sx={{ textTransform: "none" }}
                startIcon={<FontAwesomeIcon icon={faCircleQuestion} />}
                onClick={() => setStep("howItWorks")}
              >
                <FormattedMessage
                  id="voice-enrollment.how-it-works"
                  defaultMessage="How it works"
                />
              </Button>
              <Button
                sx={{ textTransform: "none" }}
                startIcon={<FontAwesomeIcon icon={faMicrophone} />}
                onClick={() => {
                  setConsent(false);
                  setStep("intro");
                }}
              >
                <FormattedMessage
                  id="voice-enrollment.record-again"
                  defaultMessage="Record my voice again"
                />
              </Button>
              <Button
                color="error"
                sx={{ textTransform: "none" }}
                startIcon={<FontAwesomeIcon icon={faTrashCan} />}
                onClick={() => setConfirmDelete(true)}
              >
                <FormattedMessage
                  id="voice-enrollment.delete"
                  defaultMessage="Delete my voice"
                />
              </Button>
            </Stack>
          )}
        </>
      );
      break;
  }

  return (
    <Modal
      open={open}
      onClose={recording || step === "uploading" ? undefined : close}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(640px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          p: { xs: 3, sm: 5 },
        }}
      >
        {step !== "uploading" && (
          <IconButton
            onClick={close}
            title={intl.formatMessage({
              id: "actions.close",
              defaultMessage: "Close",
            })}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: 18 }} />
          </IconButton>
        )}
        {content}
      </Box>
    </Modal>
  );
};
